/**
 * Autocomplete Input Component
 *
 * A reusable autocomplete component using react-native-autocomplete-dropdown v4.4
 * - Shows filtered options as user types
 * - Allows selection from list OR manual entry
 * - Theme-aware styling
 * - Web-compatible implementation using Platform detection
 *
 * Usage:
 *   <AutocompleteInput
 *     label="Sector"
 *     value={sector}
 *     onChange={setSector}
 *     options={sectors}
 *     placeholder="Select or enter sector"
 *     allowCustom={true}
 *   />
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Platform,
} from 'react-native';
import { AutocompleteDropdown } from 'react-native-autocomplete-dropdown';
import { useTheme } from '../../contexts/ThemeContext';

export interface AutocompleteOption {
  id: string;
  title: string;
}

export interface AutocompleteInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: string[] | AutocompleteOption[];
  placeholder?: string;
  allowCustom?: boolean; // Allow values not in options list
  required?: boolean;
  disabled?: boolean;
  error?: string;
  testID?: string;
  displayValue?: string; // Display text (e.g., translated) different from stored value
}

export function AutocompleteInput({
  label,
  value,
  onChange,
  options,
  placeholder,
  allowCustom = true,
  required = false,
  disabled = false,
  error,
  testID,
  displayValue,
}: AutocompleteInputProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(displayValue || value || '');
  const dropdownRef = useRef<any>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDropdownClickRef = useRef(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Normalize options to the format expected by AutocompleteDropdown
  const dataset = useMemo(() => {
    if (options.length === 0) return [];

    // If options are strings, convert to { id, title } format
    if (typeof options[0] === 'string') {
      return (options as string[])
        .filter((opt) => opt != null && opt !== '')
        .map((opt) => ({
          id: String(opt),
          title: String(opt),
        }));
    }

    // If options are already objects, ensure they have id and title
    return (options as AutocompleteOption[])
      .filter((opt) => opt != null)
      .map((opt) => {
        const id = opt.id || opt.value || String(Date.now() + Math.random());
        const title = opt.title || opt.value || opt.id || '';
        return {
          id: String(id),
          title: String(title || ''),
        };
      });
  }, [options]);

  // Update the dropdown when value prop changes
  useEffect(() => {
    const textToShow = displayValue || value || '';
    if (textToShow !== inputValue) {
      setInputValue(textToShow);
      // Also update the dropdown's internal state on native via ref
      if (Platform.OS !== 'web' && dropdownRef.current) {
        dropdownRef.current.setInputText(textToShow);
      }
    }
  }, [value, displayValue]);

  // Filter options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue) return dataset;
    const lowerInput = inputValue.toLowerCase();
    return dataset.filter((opt) => opt.title?.toLowerCase().includes(lowerInput));
  }, [dataset, inputValue]);

  const handleSelectItem = useCallback(
    (item: any) => {
      if (item) {
        isDropdownClickRef.current = true;
        // Use id for the value (English), title for display (translated)
        const selectedValue = item.id || item.title || String(item);
        const displayText = item.title || item.id || String(item);
        onChange(selectedValue);
        setInputValue(displayText);
        setIsOpen(false);
        // Reset flag after a short delay
        setTimeout(() => {
          isDropdownClickRef.current = false;
        }, 100);
      }
    },
    [onChange]
  );

  const handleInputChange = useCallback(
    (text: string) => {
      setInputValue(text);
      setIsOpen(true);
      // If allowCustom, update parent with typed value
      if (allowCustom) {
        onChange(text);
      }
    },
    [allowCustom, onChange]
  );

  const handleFocus = useCallback(() => {
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    // Delay closing to allow dropdown item clicks to process
    blurTimeoutRef.current = setTimeout(() => {
      if (!isDropdownClickRef.current) {
        setIsOpen(false);
      }
    }, 150);
  }, []);

  // Web platform: use custom dropdown implementation
  if (Platform.OS === 'web') {
    return (
      <View style={styles.container} testID={testID}>
        {label ? (
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {label} {required && <Text style={{ color: theme.colors.error }}>*</Text>}
          </Text>
        ) : null}

        <View style={styles.dropdownContainer}>
          <TextInput
            style={[
              styles.webInput,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: error ? theme.colors.error : theme.colors.border,
                color: theme.colors.text,
              },
            ]}
            value={inputValue}
            onChangeText={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            placeholderTextColor={theme.colors.textTertiary}
            editable={!disabled}
          />

          {isOpen && filteredOptions.length > 0 && (
            <View
              style={[
                styles.webDropdown,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                },
              ]}
              onStartShouldSetResponder={() => true}
            >
              <ScrollView style={styles.webDropdownScroll} keyboardShouldPersistTaps="handled">
                {filteredOptions.map((item) => {
                  const displayTitle = item?.title || '';
                  if (!displayTitle) return null;
                  return (
                    <TouchableOpacity
                      key={item.id || Math.random().toString()}
                      style={[styles.webDropdownItem, { borderBottomColor: theme.colors.border }]}
                      onPressIn={() => {
                        isDropdownClickRef.current = true;
                      }}
                      onPress={() => handleSelectItem(item)}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.webDropdownText, { color: theme.colors.text }]}>
                        {displayTitle}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          )}
        </View>

        {error && <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>}
      </View>
    );
  }

  // Native platform: use react-native-autocomplete-dropdown

  return (
    <View style={styles.container} testID={testID}>
      {label ? (
        <Text style={[styles.label, { color: theme.colors.text }]}>
          {label} {required && <Text style={{ color: theme.colors.error }}>*</Text>}
        </Text>
      ) : null}

      <View style={styles.dropdownContainer}>
        <AutocompleteDropdown
          ref={dropdownRef}
          data={dataset}
          initialValue={displayValue || value || ''}
          onSelectItem={handleSelectItem}
          inputContainerStyle={[
            styles.inputContainer,
            {
              backgroundColor: theme.colors.backgroundElevated,
              borderColor: error ? theme.colors.error : theme.colors.border,
            },
          ]}
          textInputProps={{
            placeholder: placeholder,
            placeholderTextColor: theme.colors.textTertiary,
            editable: !disabled,
            autoCorrect: false,
            autoCapitalize: 'none',
            style: {
              color: theme.colors.text,
              fontSize: theme.typography.fontSizeMd || 16,
            },
          }}
          suggestionsListContainerStyle={[
            styles.suggestionsList,
            {
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
            },
          ]}
          suggestionsListMaxHeight={200}
          renderItem={(item: any, index: number) => (
            <TouchableOpacity
              style={[
                styles.suggestionItem,
                index < dataset.length - 1 && {
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                },
              ]}
              onPress={() => handleSelectItem(item)}
            >
              <Text style={[styles.suggestionText, { color: theme.colors.text }]}>
                {item.title}
              </Text>
            </TouchableOpacity>
          )}
          emptyResultText="No results found"
          emptyResultContainerStyle={{
            padding: 16,
          }}
          emptyResultTextStyle={{
            color: theme.colors.textSecondary,
            fontSize: theme.typography.fontSizeSm || 14,
            textAlign: 'center',
          }}
        />
      </View>

      {error && <Text style={[styles.errorText, { color: theme.colors.error }]}>{error}</Text>}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginBottom: theme.spacing.md || 16,
      zIndex: 1,
    },
    webInput: {
      height: 48,
      borderRadius: theme.radii.md || 8,
      borderWidth: 1,
      paddingHorizontal: 16,
      fontSize: theme.typography.fontSizeMd || 16,
    },
    webDropdown: {
      borderWidth: 1,
      borderRadius: theme.radii.md || 8,
      marginTop: 4,
      maxHeight: 200,
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      zIndex: 1000,
    },
    webDropdownScroll: {
      maxHeight: 200,
    },
    webDropdownItem: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
    },
    webDropdownText: {
      fontSize: theme.typography.fontSizeMd || 16,
    },
    webEmptyText: {
      fontSize: theme.typography.fontSizeSm || 14,
      padding: 16,
      textAlign: 'center',
    },
    label: {
      fontSize: theme.typography.fontSizeSm || 14,
      fontWeight: theme.typography.fontWeightSemibold || '600',
      marginBottom: (theme.spacing.xs || 8) / 2,
    },
    dropdownContainer: {
      position: 'relative',
    },
    inputContainer: {
      height: 48,
      borderRadius: theme.radii.md || 8,
      borderWidth: 1,
      paddingHorizontal: 16,
    },
    suggestionsList: {
      borderWidth: 1,
      borderRadius: theme.radii.md || 8,
      marginTop: 4,
      elevation: 5,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
    },
    suggestionItem: {
      paddingVertical: 12,
      paddingHorizontal: 16,
    },
    suggestionText: {
      fontSize: theme.typography.fontSizeMd || 16,
    },
    errorText: {
      fontSize: theme.typography.fontSizeSm || 14,
      marginTop: 4,
    },
  });
}
